/**
 * CORE-AC — what a displayed finish time rests on.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHY THIS EXISTS
 * ═══════════════════════════════════════════════════════════════════════════
 * CORE-AC's remaining constitutional item is one sentence: a finish time that
 * inherits a technician's efficiency must disclose when that efficiency was
 * **manually asserted rather than learned**.
 *
 * Until `20261123120000` it could not be honoured, because a typed efficiency
 * and a learned one were the same column with nothing to tell them apart. They
 * are now distinguishable — `technicians.efficiency_source` is `manual`,
 * `learned`, `imported`, or NULL — so this module turns that column into the
 * one sentence a person reading a time needs.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * EFFICIENCY IS A DERIVATION. IT IS NEVER A MEASUREMENT.
 * ═══════════════════════════════════════════════════════════════════════════
 * CORE-G's own provenance table settles the vocabulary, and the first
 * version of this module got it wrong by calling a learned value
 * `isMeasured`:
 *
 *   Productive execution time   Consumes   **Measurement**
 *   Expected/booked time        Consumes   **Assertion (catalogue)**
 *   Authoritative efficiency    Durable    **Derivation**
 *
 * A learned efficiency is a derivation OVER a measurement and an assertion
 * — book time is somebody's estimate of how long a job should take, and no
 * amount of learning turns that into something anybody measured. So the
 * distinction this module draws is whether the value is ESTABLISHED, which
 * is CORE-G's own word, not whether it was measured.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * FOUR PROVENANCES, AND THE LAST IS THE ONE THAT MATTERS
 * ═══════════════════════════════════════════════════════════════════════════
 * CORE-G's law: it "MUST NOT present a value derived from insufficient evidence
 * as though it were established". The 2026-09-07 ruling is sharper still:
 * missing efficiency is **unknown, not 100%**, and a consumer may use a clearly
 * scoped fallback but "must not convert absence into a claimed measured 100%".
 *
 * So `unrecorded` is not a tidier way of saying `manual`. A technician created
 * by an invitation, a role change or an untouched roster row carries 100
 * because a row needs a number — nobody decided it, nobody measured it, and the
 * finish time that inherits it must say so rather than implying either.
 *
 * This module is pure and says nothing about layout. It answers only: what is
 * this number, and how confident may a reader be in it?
 */

/**
 * What the efficiency behind a displayed time actually is.
 *
 *   derived     learned by CORE-AN from completed work — CORE-G's Derivation
 *   asserted    a person stated it
 *   imported    it came from another system
 *   unrecorded  nobody established it and a fallback was used
 */
export type EfficiencyBasis = 'derived' | 'asserted' | 'imported' | 'unrecorded';

/**
 * Core-100 R4 — CORE-BB-GAP-004 / OD-09 (decision 22b): a learned efficiency is PROVISIONAL while every completion
 * that built it was applied at the original algorithm's early-learning rate (alpha 60% for the first 20 completed
 * jobs — `getBaseAlpha` in efficiencyLearningEngine.ts and `core_an_apply_efficiency_learning`). Not a new minimum:
 * OD-09 was answered "the original algorithm and its alpha schedule are the sample basis". The value is still used;
 * it is labelled provisional and never presented as established. Same boundary as the Edge resolver
 * (`supabase/functions/_shared/efficiencyResolution.ts`), pinned by efficiencyProvisionalParity.test.ts.
 */
export const LEARNED_EFFICIENCY_EARLY_LEARNING_MAX_SAMPLE = 20;

export function isProvisionalLearnedSample(sampleSize: number | null | undefined): boolean {
  return sampleSize == null || sampleSize <= LEARNED_EFFICIENCY_EARLY_LEARNING_MAX_SAMPLE;
}

export interface EfficiencyDisclosure {
  basis: EfficiencyBasis;
  /** Short badge text, sitting beside the time. */
  label: string;
  /** The full sentence — tooltip, and the accessible description. */
  detail: string;
  /**
   * True only when the value was ESTABLISHED from completed work — CORE-G's
   * own word, in "MUST NOT present a value derived from insufficient evidence
   * as though it were established".
   *
   * Deliberately NOT called `isMeasured`. Efficiency is a Derivation over a
   * measured execution time and an ASSERTED book time; calling it measured
   * would claim an SP-05 class the value does not have.
   */
  isEstablished: boolean;
  /** R4 (decision 22b): derived, but still inside the algorithm's early-learning band (or sample unrecorded). */
  isProvisional: boolean;
}

export interface EfficiencyBasisInput {
  efficiencyPercentage?: number | null;
  /** `technicians.efficiency_source` — CORE-G's provenance. */
  efficiencySource?: string | null;
  /** `technicians.efficiency_sample_size` — CORE-G's sample basis. */
  efficiencySampleSize?: number | null;
}

/**
 * Describe the efficiency a displayed time inherited.
 *
 * The input must be the SAME technician record that produced the time. A badge
 * resolved from a different or staler source would be worse than none: it would
 * describe a number the reader is not looking at.
 */
export function describeEfficiencyBasis(
  input: EfficiencyBasisInput,
): EfficiencyDisclosure {
  const { efficiencySource, efficiencySampleSize, efficiencyPercentage } = input;

  if (efficiencySource === 'learned') {
    // CORE-G "SHALL state its sample basis, and MUST NOT present a value
    // derived from insufficient evidence as though it were established", so the
    // count travels with the claim rather than being implied by the word.
    const n = efficiencySampleSize ?? 0;
    const provisional = isProvisionalLearnedSample(efficiencySampleSize);
    return {
      basis: 'derived',
      // Founder 2026-10-15 (CORE-C): a learned efficiency is a DETERMINISTIC derivation (CORE-AN: a defined weighted update of
      // measured visits) — "Calculated by OptiBay", not an inference; its sample and provisional state (the uncertainty)
      // travel with it in `detail` and in the "· provisional" suffix.
      label: provisional ? 'Calculated by OptiBay · provisional' : 'Calculated by OptiBay',
      detail:
        n > 0
          ? `This finish time uses an efficiency derived from ${n} completed ${n === 1 ? 'visit' : 'visits'}.` +
            (provisional
              ? ` It is provisional: the learning still weights each of the first ${LEARNED_EFFICIENCY_EARLY_LEARNING_MAX_SAMPLE} visits strongly, so it can move noticeably with the next one.`
              : '')
          : 'This finish time uses a derived efficiency, but its sample basis was not recorded, so it is provisional.',
      isEstablished: !provisional,
      isProvisional: provisional,
    };
  }

  if (efficiencySource === 'manual') {
    return {
      basis: 'asserted',
      label: 'Set by a person',
      detail:
        'This finish time uses an efficiency a person stated, not one derived from ' +
        'completed work. It is an estimate until enough work has been done to learn from.',
      isEstablished: false,
      isProvisional: false,
    };
  }

  if (efficiencySource === 'imported') {
    return {
      basis: 'imported',
      // OD-35: supplied by another system — reported, not established here.
      label: 'Reported by External Source',
      detail:
        'This finish time uses an efficiency imported from another system, not one ' +
        'derived from work done here.',
      isEstablished: false,
      isProvisional: false,
    };
  }

  // Nobody established it. The number the time used is a fallback, and saying
  // so is the whole point of this state existing.
  const shown =
    typeof efficiencyPercentage === 'number' ? efficiencyPercentage : null;
  return {
    basis: 'unrecorded',
    label: 'Not established',
    detail:
      shown === null
        ? 'No efficiency has been recorded for this technician, so this finish ' +
          'time uses a default rather than anything derived or stated.'
        : `No efficiency has been recorded for this technician. This finish ` +
          `time uses ${shown}% as a default, which nobody derived and nobody ` +
          'stated.',
    isEstablished: false,
    isProvisional: false,
  };
}

/**
 * Core-100 R4 W5 — CORE-AW Prov (GAP-001/005): a technician's capacity in minutes is presence × efficiency × the
 * shop's reserve, so it inherits the efficiency's basis. The per-technician capacity line states that basis beside
 * the minutes — and says "estimate" whenever the efficiency is not established (provisional, stated by a person,
 * imported or unrecorded), so capacity never implies precision it does not have.
 */
export function describeCapacityBasis(tech: {
  efficiency_source?: string | null; efficiency_sample_size?: number | null; efficiency_percentage?: number | null;
}): { text: string; detail: string; isEstimate: boolean } {
  const d = describeEfficiencyBasis({
    efficiencySource: tech.efficiency_source ?? null,
    efficiencySampleSize: tech.efficiency_sample_size ?? null,
    efficiencyPercentage: tech.efficiency_percentage ?? null,
  });
  const n = tech.efficiency_sample_size ?? 0;
  const sample = d.basis === 'derived' && n > 0 ? ` from ${n} ${n === 1 ? 'visit' : 'visits'}` : '';
  return {
    text: `Efficiency: ${d.label}${sample}${d.isEstablished ? '' : ' — capacity is an estimate'}`,
    detail: d.detail,
    isEstimate: !d.isEstablished,
  };
}

/**
 * Core-100 R2 — CORE-AV-GAP-001: a load figure built on OptiTime inherits every working
 * technician's efficiency basis. Summarised so a load surface can say how much of it rests on
 * efficiency learned from work and how much on typed or unrecorded values — never implying the
 * load is measured when it is not.
 */
export function summarizeEfficiencyBasis(
  techs: ReadonlyArray<{ efficiency_source?: string | null; efficiency_sample_size?: number | null; efficiency_percentage?: number | null }>,
): { derived: number; asserted: number; imported: number; unrecorded: number; provisional: number; total: number; sentence: string } {
  const out = { derived: 0, asserted: 0, imported: 0, unrecorded: 0 };
  let provisional = 0;
  for (const t of techs) {
    const d = describeEfficiencyBasis({
      efficiencySource: t.efficiency_source ?? null,
      efficiencySampleSize: t.efficiency_sample_size ?? null,
      efficiencyPercentage: t.efficiency_percentage ?? null,
    });
    out[d.basis] += 1;
    if (d.isProvisional) provisional += 1;
  }
  const total = techs.length;
  const typed = out.asserted + out.imported + out.unrecorded;
  const sentence = total === 0
    ? 'No working technicians, so no efficiency is involved.'
    : out.derived === total
      ? `Every working technician's efficiency was learned from completed work.`
      : `${out.derived} of ${total} working technicians' efficiency was learned from completed work; ${typed} ${typed === 1 ? 'is' : 'are'} typed or unrecorded, so this load is an estimate built on them.`;
  // R4 (decision 22b): learned is not the same as settled — say how many learned values are still provisional.
  const provisionalNote = provisional > 0
    ? ` ${provisional} learned ${provisional === 1 ? 'value is' : 'values are'} still provisional (${LEARNED_EFFICIENCY_EARLY_LEARNING_MAX_SAMPLE} or fewer completed visits).`
    : '';
  return { ...out, provisional, total, sentence: sentence + provisionalNote };
}
